/**
 * Job openings of the institutional site ("vagas", careers; owner request 2026-10-19). An operator publishes an opening from the Admin;
 * the site lists the OPEN ones for a COUNTRY (and, when the opening names one, a city / operating market).
 *
 * Life of an opening:
 *
 *   DRAFT ──open──▶ OPEN ──close──▶ CLOSED        any status may move to any other (a closed vacancy can go back to a draft,
 *     ▲               │                           an open one back to a draft to be reworked); the same status twice is refused.
 *     └───────────────┘
 *
 * - Only an OPEN opening is public. A DRAFT is being written; a CLOSED one is kept for the record and no longer listed.
 * - The words (title, team, employment type, summary, description, market, apply link) are editable while the opening is a DRAFT or
 *   OPEN; a CLOSED opening is frozen (`CAREER_OPENING_CLOSED`) - bring it back to a draft first.
 * - `openedAt` is the instant it was (last) opened; `closedAt` the instant it was closed. A draft carries neither.
 * - Every actor (`createdBy`, `updatedBy`) is the operator's session, never a body field.
 *
 * Neutral: the country is an ISO 3166-1 alpha-2 code, the market an operating-market id (`cc:slug`) that belongs to that country,
 * the employment type free text (a hiring regime is data, never an enum), the texts in whatever language the operator writes.
 * No country's labour law, currency or language is named here.
 */
export const CAREER_OPENING_STATUSES = ['DRAFT', 'OPEN', 'CLOSED'];
/** Stable refusals of the career-opening routes (answered as `problem.reasonCode`). */
export const CAREER_REFUSALS = [
    'CAREER_OPENING_INVALID',
    'CAREER_OPENING_NOT_FOUND',
    /** A CLOSED opening is frozen: bring it back to a draft before editing it. */
    'CAREER_OPENING_CLOSED',
    /** The market is not registered, or belongs to another country than the opening. */
    'CAREER_OPENING_MARKET_INVALID',
    /** The opening is already in the requested status. */
    'CAREER_OPENING_STATUS_UNCHANGED',
];
/** The stable reason code of a successful status change. */
export const CAREER_OPENING_STATUS_CHANGED = 'CAREER_OPENING_STATUS_CHANGED';
export const CAREER_TITLE_MIN = 3;
export const CAREER_TITLE_MAX = 120;
export const CAREER_TEAM_MAX = 60;
export const CAREER_EMPLOYMENT_TYPE_MAX = 40;
export const CAREER_SUMMARY_MAX = 280;
export const CAREER_DESCRIPTION_MAX = 5000;
export const CAREER_APPLY_URL_MAX = 500;
const COUNTRY = /^[A-Z]{2}$/;
const MARKET_ID = /^[a-z]{2}:[a-z0-9]+(-[a-z0-9]+)*$/;
/** An https URL or a mailto address, no whitespace or control character inside. */
const APPLY_URL = /^(https:\/\/[^\s/?#][^\s]*|mailto:[^\s@]+@[^\s@]+)$/;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f-\u009f]/;
// eslint-disable-next-line no-control-regex
const CONTROL_EXCEPT_LINE_BREAKS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f]/;
export const isCareerOpeningStatus = (v) => typeof v === 'string' && CAREER_OPENING_STATUSES.includes(v);
export const isCountryCode = (v) => typeof v === 'string' && COUNTRY.test(v);
/** An operating-market id belongs to a country when its `cc:` prefix is that country's code (lower case). */
export const marketBelongsToCountry = (marketId, country) => MARKET_ID.test(marketId) && marketId.slice(0, 2) === country.toLowerCase();
const bounded = (v, min, max, singleLine) => {
    if (typeof v !== 'string')
        return undefined;
    const text = singleLine ? v.trim() : v.replace(/\r\n?/g, '\n').trim();
    if (text.length < min || text.length > max)
        return undefined;
    if ((singleLine ? CONTROL : CONTROL_EXCEPT_LINE_BREAKS).test(text))
        return undefined;
    return text;
};
/** `https://` or `mailto:` only (never `javascript:`, `data:`, `http:`), at most 500 characters; undefined when it is neither. */
export function validateApplyUrl(v) {
    if (typeof v !== 'string')
        return undefined;
    const url = v.trim();
    if (url.length === 0 || url.length > CAREER_APPLY_URL_MAX || !APPLY_URL.test(url))
        return undefined;
    if (url.startsWith('https://')) {
        try {
            if (new URL(url).hostname === '')
                return undefined;
        }
        catch {
            return undefined;
        }
    }
    return url;
}
const absent = (v) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
/**
 * The editable words of an opening, validated and trimmed - or undefined when anything is out of bounds. `marketId` and
 * `applyUrl` are optional (absent, `null` or an empty string mean "none"). A market must be a well-formed `cc:slug` id; that it is
 * registered and belongs to the opening's country is decided by the caller ({@link marketBelongsToCountry} + the market registry),
 * which answers `CAREER_OPENING_MARKET_INVALID`.
 */
export function validateCareerContent(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return undefined;
    const o = raw;
    const title = bounded(o.title, CAREER_TITLE_MIN, CAREER_TITLE_MAX, true);
    const team = bounded(o.team, 1, CAREER_TEAM_MAX, true);
    const employmentType = bounded(o.employmentType, 1, CAREER_EMPLOYMENT_TYPE_MAX, true);
    const summary = bounded(o.summary, 1, CAREER_SUMMARY_MAX, true);
    const description = bounded(o.description, 1, CAREER_DESCRIPTION_MAX, false);
    if (title === undefined || team === undefined || employmentType === undefined || summary === undefined || description === undefined)
        return undefined;
    let marketId;
    if (!absent(o.marketId)) {
        if (typeof o.marketId !== 'string' || !MARKET_ID.test(o.marketId.trim()))
            return undefined;
        marketId = o.marketId.trim();
    }
    let applyUrl;
    if (!absent(o.applyUrl)) {
        applyUrl = validateApplyUrl(o.applyUrl);
        if (applyUrl === undefined)
            return undefined;
    }
    return { title, team, employmentType, summary, description, ...(marketId !== undefined ? { marketId } : {}), ...(applyUrl !== undefined ? { applyUrl } : {}) };
}
/** A new opening's input (country + words), validated. */
export function validateCareerOpeningInput(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return undefined;
    const country = raw.country;
    if (!isCountryCode(country))
        return undefined;
    const content = validateCareerContent(raw);
    return content === undefined ? undefined : { country, ...content };
}
/** The timestamps a status carries: DRAFT neither; OPEN `openedAt` (now); CLOSED `closedAt` (now) and the `openedAt` it had. */
export function statusTimestamps(to, current, at) {
    if (to === 'DRAFT')
        return {};
    if (to === 'OPEN')
        return { openedAt: at };
    return { ...(current.openedAt !== undefined ? { openedAt: current.openedAt } : {}), closedAt: at };
}
/** Refuse a change to the status the opening already has. */
export function statusChangeRefusal(from, to) {
    return from === to ? 'CAREER_OPENING_STATUS_UNCHANGED' : undefined;
}
/** The public projection of an OPEN opening; `marketName` comes from the market registry. Undefined for any other status. */
export function toPublicCareerOpening(o, marketName) {
    if (o.status !== 'OPEN' || o.openedAt === undefined)
        return undefined;
    return {
        openingId: o.openingId, title: o.title, team: o.team, employmentType: o.employmentType, summary: o.summary, description: o.description,
        ...(o.marketId !== undefined ? { marketId: o.marketId } : {}),
        ...(o.marketId !== undefined && marketName !== undefined ? { marketName } : {}),
        ...(o.applyUrl !== undefined ? { applyUrl: o.applyUrl } : {}),
        openedAt: o.openedAt,
    };
}
//# sourceMappingURL=career-openings.js.map